import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { EstoqueLoteSerieInlineComponent } from '@shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { LoteSaidaEstoqueModalItem } from '@shared/components/lote-saida-estoque-modal/lote-saida-estoque-modal.component';
import { SerieSaidaEstoqueModalItem } from '@shared/components/serie-saida-estoque-modal/serie-saida-estoque-modal.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { OrigemLoteSerieEstoqueEnum } from '@shared/enums/produto/origem-lote-serie-estoque.enum';
import { TipoLancamentoEstoqueFabricadoEnum } from '@shared/enums/vendas/tipo-lancamento-estoque-fabricado.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { LookupService as ProdutoLookupService } from '@shared/services/apis/produto/produto-lookup.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { LancarEstoqueVendaRequest } from '@shared/services/requests/vendas/lancar-estoque-venda.request';
import { LookupItemStruct } from '@shared/services/structs/pessoa/lookup-item.struct';
import { EstoquePedidoVendaLinhaStruct } from '@shared/services/structs/produto/estoque-pedido-venda-linha.struct';
import { EstoquePedidoVendaPendenciaStruct } from '@shared/services/structs/produto/estoque-pedido-venda-pendencia.struct';
import { LoteDisponivelLookupStruct } from '@shared/services/structs/produto/lote-disponivel-lookup.struct';
import { UtilService } from '@shared/services/util.service';

type ItemPainel = LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem;

/** Estado do painel de lote/série de uma linha: a seleção inicial, a lista com saldo (nula enquanto carrega) e o lookup dos lotes. */
interface PainelLinha {
    itens: ItemPainel[];
    disponiveis: ItemPainel[] | null;
    lotesDoLookup: LoteDisponivelLookupStruct[];
}

export interface PvLancarEstoqueDialogData {
    idVenda: number;
    /** "Pedido nº 123" — o rótulo do cabeçalho. */
    rotuloDocumento: string;
}

/**
 * Lançar estoque do pedido (T10): o Produto monta a prévia (local do item ou padrão; lote e série do item, da reserva
 * ou da sugestão pelo que vence primeiro) e o usuário completa aqui o que faltar: troca o local e escolhe os lotes ou
 * séries pelo mesmo painel do modal do item. Com a configuração "Escolher na venda" e item fabricado, pergunta se sai o
 * produto fabricado ou os componentes. Tudo ou nada: com linha incompleta o Lançar fica desabilitado. Fecha com `true`
 * quando lançou.
 */
@Component({
    selector: 'app-pv-lancar-estoque-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        SelectPesquisavelComponent,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatRadioModule,
        AlertCardComponent,
        LoadingBoxComponent,
        AbntPipe,
        EstoqueLoteSerieInlineComponent,
    ],
    templateUrl: './pv-lancar-estoque-dialog.component.html',
    styleUrls: ['./pv-lancar-estoque-dialog.component.css'],
})
export class PvLancarEstoqueDialogComponent implements OnInit {
    public isLoading = true;
    public isLancando = false;
    public linhas: EstoquePedidoVendaLinhaStruct[] = [];
    public pendencias: EstoquePedidoVendaPendenciaStruct[] = [];
    public locais: LookupItemStruct[] = [];

    public precisaEscolherFabricado = false;
    public idTipoLancamentoEstoqueFabricado = 0;
    public escolhaFabricado: TipoLancamentoEstoqueFabricadoEnum = TipoLancamentoEstoqueFabricadoEnum.ProdutoFabricado;
    public readonly tipoLancamentoEstoqueFabricadoEnum = TipoLancamentoEstoqueFabricadoEnum;

    /** Linha com o painel de lote/série aberto (chave `idVendaItem:idProduto`). */
    public linhaAberta: string | null = null;

    /**
     * Painel de lote/série de cada linha, por chave: a seleção inicial só muda ao carregar ou trocar o local, para o
     * painel não receber de volta a seleção que ele mesmo emitiu (refaria as linhas e perderia as incompletas).
     */
    private painelPorChave = new Map<string, PainelLinha>();

    /** Controle do local por linha: o select pesquisável trabalha com FormControl, e a troca segue para a linha. */
    private controleLocalPorChave = new Map<string, FormControl<number | null>>();

    constructor(
        public dialogRef: MatDialogRef<PvLancarEstoqueDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: PvLancarEstoqueDialogData,
        private processoVendaService: ProcessoVendaService,
        private produtoLookupService: ProdutoLookupService,
        private alertService: AlertService,
        private authService: AuthService,
    ) {}

    public ngOnInit(): void {
        this.produtoLookupService.getLocalizacaoEstoqueSelect().subscribe({
            next: (res) => {
                this.locais = res.isError ? [] : res.list ?? [];
                if (res.isError) {
                    this.alertService.show('Aviso',
                        res.errorDescription || 'Não foi possível carregar os locais de estoque. Feche e abra o diálogo de novo.',
                        AlertType.warning);
                }
            },
            error: () => this.alertService.show('Aviso',
                'Não foi possível carregar os locais de estoque agora. Feche e abra o diálogo de novo.', AlertType.warning),
        });
        this.carregar();
    }

    /** Sem a prévia do servidor não há o que mostrar nem confirmar: avisa por toast e fecha. */
    public carregar(): void {
        this.isLoading = true;
        const tipo = this.precisaEscolherFabricado
            || this.idTipoLancamentoEstoqueFabricado === TipoLancamentoEstoqueFabricadoEnum.EscolherNaVenda
            ? this.escolhaFabricado : null;

        this.processoVendaService.getEstoque(this.data.idVenda, tipo).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível consultar o estoque do pedido.',
                        AlertType.warning);
                    this.dialogRef.close(false);
                    return;
                }

                // O card estava desatualizado (automação ou outro usuário): fecha com true para quem abriu recarregar.
                if (res.isEstoqueLancado) {
                    this.alertService.show('Aviso', 'O estoque deste pedido já foi lançado.', AlertType.warning);
                    this.dialogRef.close(true);
                    return;
                }

                this.linhas = res.linhas ?? [];
                this.pendencias = res.pendencias ?? [];
                this.precisaEscolherFabricado = res.precisaEscolherFabricado;
                this.idTipoLancamentoEstoqueFabricado = res.idTipoLancamentoEstoqueFabricado;
                this.painelPorChave = new Map(this.linhas.map((l) => [this.chave(l), this.novoPainel(l)]));
                const primeiraPendente = this.linhas.find((l) => this.motivoDaLinha(l));
                this.linhaAberta = null;
                if (primeiraPendente) this.abrirLinha(primeiraPendente);
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Aviso',
                    'Não foi possível consultar o estoque do pedido agora. Tente novamente em alguns instantes.', AlertType.warning);
                this.dialogRef.close(false);
            },
        });
    }

    /** Trocar fabricado × componentes refaz a prévia: as linhas mudam (o produto ou os componentes). */
    public aoEscolherFabricado(): void {
        this.carregar();
    }

    public chave(linha: EstoquePedidoVendaLinhaStruct): string {
        return `${linha.idVendaItem}:${linha.idProduto}`;
    }

    public controleLocal(linha: EstoquePedidoVendaLinhaStruct): FormControl<number | null> {
        const chave = this.chave(linha);
        let controle = this.controleLocalPorChave.get(chave);
        if (!controle) {
            controle = new FormControl<number | null>(linha.idLocalizacaoEstoque || null);
            controle.valueChanges.subscribe((id) => {
                // A prévia recarrega as linhas: a troca vale para a linha atual da chave, não para a que criou o controle.
                const atual = this.linhas.find((l) => this.chave(l) === chave);
                if (!atual || !id || id === atual.idLocalizacaoEstoque) return;
                atual.idLocalizacaoEstoque = id;
                this.aoTrocarLocal(atual);
            });
            this.controleLocalPorChave.set(chave, controle);
        } else if ((controle.value || null) !== (linha.idLocalizacaoEstoque || null)) {
            controle.setValue(linha.idLocalizacaoEstoque || null, { emitEvent: false });
        }
        return controle;
    }

    public painel(linha: EstoquePedidoVendaLinhaStruct): PainelLinha {
        let painel = this.painelPorChave.get(this.chave(linha));
        if (!painel) {
            painel = this.novoPainel(linha);
            this.painelPorChave.set(this.chave(linha), painel);
        }
        return painel;
    }

    /** A seleção da linha no formato do painel: o lote entra com o saldo da própria seleção, para continuar disponível a ela. */
    private novoPainel(linha: EstoquePedidoVendaLinhaStruct): PainelLinha {
        const itens: ItemPainel[] = linha.isControlaSeries
            ? linha.series.map((s) => ({ idSerie: s.idSerie ?? 0, codigoSerie: s.codigoSerie ?? '', selecionada: true }))
            : linha.lotes.map((l) => ({
                idLote: l.idLote ?? 0,
                codigoLote: l.codigoLote ?? '',
                dataValidade: l.dataValidade ?? undefined,
                saldoDisponivel: Number(l.quantidade ?? 0) || 0,
                quantidadeSaida: Number(l.quantidade ?? 0) || 0,
            }));
        return { itens, disponiveis: null, lotesDoLookup: [] };
    }

    /** Lotes ou séries com saldo no local da linha, com a seleção atual mantida na lista mesmo sem saldo. */
    private carregarDisponiveis(linha: EstoquePedidoVendaLinhaStruct): void {
        const painel = this.painel(linha);
        painel.disponiveis = null;
        if (!linha.idProduto) {
            painel.disponiveis = [...painel.itens];
            return;
        }

        const falhar = (mensagem: string) => {
            this.alertService.show('Aviso', mensagem, AlertType.warning);
            painel.disponiveis = [...painel.itens];
        };

        if (linha.isControlaSeries) {
            this.produtoLookupService.getSerieDisponivelSelect(linha.idProduto, linha.idLocalizacaoEstoque, this.data.idVenda).subscribe({
                next: (res) => {
                    if (res.isError) return falhar('Não foi possível carregar as séries disponíveis do produto.');
                    const lista: SerieSaidaEstoqueModalItem[] = (res.list ?? [])
                        .map((s) => ({ idSerie: s.idSerie, codigoSerie: s.numeroSerie, selecionada: false }));
                    const selecionadas = (painel.itens as SerieSaidaEstoqueModalItem[])
                        .filter((s) => !lista.some((d) => d.idSerie === s.idSerie));
                    painel.disponiveis = [...selecionadas, ...lista];
                },
                error: () => falhar('Não foi possível carregar as séries disponíveis do produto.'),
            });
            return;
        }

        this.produtoLookupService.getLoteDisponivelSelect(linha.idProduto, linha.idLocalizacaoEstoque, this.data.idVenda).subscribe({
            next: (res) => {
                if (res.isError) return falhar('Não foi possível carregar os lotes disponíveis do produto.');
                // O lookup devolve as datas com hora; o lote do lançar é só data (DateOnly), como vem da prévia.
                painel.lotesDoLookup = (res.list ?? []).map((l) => ({
                    ...l, dataValidade: l.dataValidade?.slice(0, 10), dataFabricacao: l.dataFabricacao?.slice(0, 10) ?? null,
                }));
                const lista: LoteSaidaEstoqueModalItem[] = painel.lotesDoLookup.map((l) => ({
                    idLote: l.idLote,
                    codigoLote: l.numeroLote,
                    dataValidade: l.dataValidade,
                    saldoDisponivel: l.saldoDisponivel,
                    quantidadeSaida: 0,
                }));
                const selecionados = (painel.itens as LoteSaidaEstoqueModalItem[]).filter((l) => !lista.some((d) => d.idLote === l.idLote));
                painel.disponiveis = [...selecionados, ...lista];
            },
            error: () => falhar('Não foi possível carregar os lotes disponíveis do produto.'),
        });
    }

    /** Pendências de item sem linha correspondente (produto não encontrado, sem local padrão, sem estrutura): o diálogo não resolve. */
    public get pendenciasForaDoDialogo(): EstoquePedidoVendaPendenciaStruct[] {
        return this.pendencias.filter(
            (p) => p.idVendaItem > 0 && !this.linhas.some((l) => l.idVendaItem === p.idVendaItem && l.idProduto === p.idProduto),
        );
    }

    /**
     * Pendências do pedido inteiro, sem item (o saldo somado de todos os depósitos não cobre a saída): não travam o
     * Lançar, porque trocar o local de uma linha pode resolver; saem quando o usuário mexe em qualquer linha.
     */
    public get pendenciasDoPedido(): EstoquePedidoVendaPendenciaStruct[] {
        return this.pendencias.filter((p) => p.idVendaItem === 0);
    }

    /**
     * O que falta na linha para ela sair: a régua local (local, soma dos lotes, uma série por unidade) e, enquanto o
     * usuário não mexer na linha, a pendência que o servidor apontou para ela.
     */
    public motivoDaLinha(linha: EstoquePedidoVendaLinhaStruct): string | null {
        if (!linha.idLocalizacaoEstoque) return 'Informe o local de estoque.';

        if (linha.isControlaLotes) {
            const casas = this.authService.getUserInfos()?.casasDecimaisQuantidade ?? 2;
            const soma = linha.lotes.reduce((total, l) => total + (Number(l.quantidade ?? 0) || 0), 0);
            if (UtilService.arredondarAbnt(soma, casas) !== UtilService.arredondarAbnt(linha.quantidade, casas)) {
                return 'Selecione os lotes: a soma tem de ser igual à quantidade.';
            }
        }

        if (linha.isControlaSeries && linha.series.length !== linha.quantidade) return 'Selecione uma série por unidade.';

        const doServidor = this.pendencias.find((p) => p.idVendaItem === linha.idVendaItem && p.idProduto === linha.idProduto);
        return doServidor?.motivo ?? null;
    }

    public descricaoOrigem(linha: EstoquePedidoVendaLinhaStruct): string {
        switch (linha.origemLoteSerieEstoque) {
            case OrigemLoteSerieEstoqueEnum.Item: return linha.isControlaSeries ? 'Séries do pedido' : 'Lotes do pedido';
            case OrigemLoteSerieEstoqueEnum.Reserva:
                return linha.isControlaSeries ? 'Séries da reserva do pedido' : 'Lotes da reserva do pedido';
            case OrigemLoteSerieEstoqueEnum.Sugestao: return 'Sugestão: do lote que vence primeiro';
            default: return '';
        }
    }

    public descricaoSeries(linha: EstoquePedidoVendaLinhaStruct): string {
        return linha.series.map((s) => s.codigoSerie ?? '').filter((n) => n).join(', ');
    }

    public nomeLocal(linha: EstoquePedidoVendaLinhaStruct): string {
        return this.locais.find((l) => l.id === linha.idLocalizacaoEstoque)?.descricao ?? linha.descricaoLocalizacao;
    }

    /** O lote e a série são do local: trocar o local zera a seleção e o painel recarrega os disponíveis. */
    public aoTrocarLocal(linha: EstoquePedidoVendaLinhaStruct): void {
        linha.lotes = [];
        linha.series = [];
        linha.origemLoteSerieEstoque = null;
        linha.descricaoLocalizacao = this.nomeLocal(linha);
        this.painelPorChave.set(this.chave(linha), this.novoPainel(linha));
        this.removerPendenciaDoServidor(linha);
        if (linha.isControlaLotes || linha.isControlaSeries) this.abrirLinha(linha);
    }

    /** A seleção do painel volta para a linha no formato do Produto; a fabricação do lote vem do lookup ou da seleção anterior. */
    public aoAlterarSelecao(linha: EstoquePedidoVendaLinhaStruct, itens: ItemPainel[]): void {
        if (linha.isControlaSeries) {
            linha.series = (itens as SerieSaidaEstoqueModalItem[]).map((s) => ({ idSerie: s.idSerie, codigoSerie: s.codigoSerie }));
        } else {
            const painel = this.painel(linha);
            const anteriores = new Map(linha.lotes.map((l) => [l.idLote, l]));
            linha.lotes = (itens as LoteSaidaEstoqueModalItem[]).map((l) => ({
                idLote: l.idLote,
                codigoLote: l.codigoLote,
                quantidade: Number(l.quantidadeSaida ?? 0) || 0,
                dataValidade: l.dataValidade ?? anteriores.get(l.idLote)?.dataValidade ?? null,
                dataFabricacao: painel.lotesDoLookup.find((d) => d.idLote === l.idLote)?.dataFabricacao
                    ?? anteriores.get(l.idLote)?.dataFabricacao ?? null,
            }));
        }
        linha.origemLoteSerieEstoque = OrigemLoteSerieEstoqueEnum.Item;
        this.removerPendenciaDoServidor(linha);
    }

    /** O usuário mexeu na linha: a pendência dela e as do pedido inteiro deixam de valer até o próximo lançar. */
    private removerPendenciaDoServidor(linha: EstoquePedidoVendaLinhaStruct): void {
        this.pendencias = this.pendencias.filter(
            (p) => p.idVendaItem > 0 && !(p.idVendaItem === linha.idVendaItem && p.idProduto === linha.idProduto),
        );
    }

    public alternarLinha(linha: EstoquePedidoVendaLinhaStruct): void {
        if (this.linhaAberta === this.chave(linha)) {
            this.linhaAberta = null;
            return;
        }
        this.abrirLinha(linha);
    }

    /** Abre o painel da linha e carrega os lotes ou as séries com saldo no local dela. */
    private abrirLinha(linha: EstoquePedidoVendaLinhaStruct): void {
        this.linhaAberta = this.chave(linha);
        this.carregarDisponiveis(linha);
    }

    public get linhasIncompletas(): number {
        return this.linhas.filter((l) => this.motivoDaLinha(l)).length;
    }

    public get podeLancar(): boolean {
        return !this.isLoading && this.linhas.length > 0 && this.linhasIncompletas === 0 && this.pendenciasForaDoDialogo.length === 0;
    }

    public get tooltipLancar(): string {
        if (this.isLoading || this.podeLancar) return '';
        if (this.pendenciasForaDoDialogo.length > 0) return 'Corrija os itens com pendência pela tela do pedido, salve e lance de novo.';
        if (this.linhasIncompletas > 0) return 'Complete o local, os lotes ou as séries das linhas marcadas.';
        return 'Nenhum item do pedido movimenta estoque.';
    }

    public confirmar(): void {
        if (!this.podeLancar || this.isLancando) return;

        // As linhas voltam como itens: o componente vai com o item do pedido e o próprio produto, sem ser aberto de novo;
        // o Produto confere lote, série e saldo sob a trava.
        const request: LancarEstoqueVendaRequest = {
            idTipoLancamentoEstoqueFabricado: this.precisaEscolherFabricado ? this.escolhaFabricado : null,
            itens: this.linhas.map((l) => ({
                idVendaItem: l.idVendaItem,
                sequencia: l.sequencia,
                descricao: l.descricao,
                idProduto: l.idProduto,
                quantidade: l.quantidade,
                siglaUnidade: l.siglaUnidade,
                idLocalizacaoEstoque: l.idLocalizacaoEstoque,
                lotes: l.lotes,
                series: l.series,
            })),
        };

        this.isLancando = true;
        this.processoVendaService.lancarEstoque(this.data.idVenda, request).subscribe({
            next: (res) => {
                this.isLancando = false;

                if (res.isError) {
                    // O servidor é a verdade: se apontou pendências, a lista do diálogo passa a ser a dele.
                    if (res.pendencias?.length) this.pendencias = res.pendencias;
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível lançar o estoque do pedido.',
                        AlertType.warning);
                    return;
                }

                if (res.avisoDescricao) {
                    this.alertService.show('Aviso', res.avisoDescricao, AlertType.warning);
                } else {
                    this.alertService.show('Estoque lançado', 'O pedido fica em leitura até o estoque ser excluído.', AlertType.success);
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isLancando = false;
                this.alertService.show('Aviso',
                    'Não foi possível lançar o estoque agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close(false);
    }
}
